import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import FloatingMeetButton from './components/FloatingMeetButton';
import ScrollToTop from './components/ScrollToTop';
import Chatbot from './components/Chatbot';

// Lazy load all pages for high-performance code splitting (reducing initial bundle size)
const Home = lazy(() => import('./Pages/Home'));
const Services = lazy(() => import('./Pages/Services'));
const Products = lazy(() => import('./Pages/Products'));
const Portfolio = lazy(() => import('./Pages/Portfolio'));
const ProductDetail = lazy(() => import('./Pages/ProductDetail'));
const Testimonials = lazy(() => import('./Pages/Testimonials'));
const Blog = lazy(() => import('./Pages/Blog'));
const Contact = lazy(() => import('./Pages/Contact'));
const AdminLogin = lazy(() => import('./Pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./Pages/AdminDashboard'));
const EnquiryManagement = lazy(() => import('./Pages/EnquiryManagement'));
const UserManagement = lazy(() => import('./Pages/UserManagement'));
const Careers = lazy(() => import('./Pages/Careers'));
const JobManagement = lazy(() => import('./Pages/JobManagement'));
const TestimonialManagement = lazy(() => import('./Pages/TestimonialManagement'));
const BlogManagement = lazy(() => import('./Pages/BlogManagement'));

// A clean, lightweight modern spinner for loading states
const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <div className="App">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/enquiry/:id"
                  element={
                    <ProtectedRoute>
                      <EnquiryManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/jobs"
                  element={
                    <ProtectedRoute>
                      <JobManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/testimonials"
                  element={
                    <ProtectedRoute>
                      <TestimonialManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/blogs"
                  element={
                    <ProtectedRoute>
                      <BlogManagement />
                    </ProtectedRoute>
                  }
                />

                {/* Public Routes */}
                <Route
                  path="/*"
                  element={
                    <>
                      <Navigation />
                      <main style={{ minHeight: '75vh' }}>
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/products" element={<Products />} />
                            <Route path="/products/:productId" element={<ProductDetail />} />
                            <Route path="/product/:productId" element={<ProductDetail />} />
                            <Route path="/product" element={<Navigate to="/products" replace />} />
                            <Route path="/portfolio" element={<Portfolio />} />
                            <Route path="/testimonials" element={<Testimonials />} />
                            <Route path="/blogs" element={<Blog />} />
                            <Route path="/blogs/:slug" element={<Blog />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/careers" element={<Careers />} />
                          </Routes>
                        </Suspense>
                      </main>
                      <Footer />
                      <FloatingMeetButton />
                      <Chatbot />
                    </>
                  }
                />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
